import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { SendWindowForm } from '@/settings/send-window/form'
import type { SendWindowValue } from '@/settings/send-window/form'
import { trimSeconds } from '@/settings/send-window/times'
import { summariseSendWindow } from '@/settings/send-window/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { useOneEditing } from '@/ui/useOneEditing'
import { ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Settings › Outreach › Send window.
 *
 * Was `pages/outreach/SendWindowPanel` on the Account health screen until the
 * settings consolidation of 2026-09-15. The permission is the automation
 * switch's — `budget:manage` — for the reason `app/v1/automation.py` gives:
 * narrowing the window stops automation the way pausing does.
 */
export function SendWindowPanel(): JSX.Element {
  const { membership } = useActiveTenant()
  const editable = may(membership, 'budget:manage')
  const toast = useToast()
  const one = useOneEditing()
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const automation = useQuery((signal) => call(() => api.GET('/api/v1/automation', { signal })), [])
  const save = useMutation((body: SendWindowValue) =>
    call(() => api.PUT('/api/v1/automation/send-window', { body })),
  )

  if (automation.state.status === 'loading') {
    return <Loading label="Loading your send window" lines={2} />
  }
  if (automation.state.status === 'error') {
    return (
      <ErrorState
        failure={automation.state.failure}
        title="Could not load your send window"
        onRetry={automation.refetch}
      />
    )
  }
  const current = automation.state.data
  const value: SendWindowValue = {
    window_start: trimSeconds(current.window_start),
    window_end: trimSeconds(current.window_end),
    skip_weekends: current.skip_weekends,
  }

  return (
    <div className="ar-stack">
      <h2 className="ar-h2">Send window</h2>
      <SettingRow
        label="Send window"
        summary={summariseSendWindow(current)}
        savedAt={savedAt}
        editable={editable}
        editing={one.editing === 'window'}
        onEdit={() => {
          one.open('window')
        }}
        onCancel={one.close}
      >
        <SendWindowForm
          value={value}
          submitLabel="Save"
          onSubmit={async (next) => {
            const result = await save.run(next)
            if (result.ok) {
              setSavedAt(result.data.changed_at ?? new Date().toISOString())
              one.close()
              automation.refetch()
              toast.show({
                kind: 'success',
                title: 'Send window saved',
                body: next.skip_weekends
                  ? `Automation acts between ${next.window_start} and ${next.window_end} IST, weekdays only.`
                  : `Automation acts between ${next.window_start} and ${next.window_end} IST, every day.`,
              })
            }
            return result
          }}
        />
      </SettingRow>
    </div>
  )
}
